import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { Form, Input, Button, message, Progress, Radio, Checkbox, Upload, Spin, Alert } from 'antd';
import { PlusOutlined, LoadingOutlined } from '@ant-design/icons';
import { submitConsultationForm, getTemplateData, TemplateData } from '../../services/consultationService';
import { getOSSPolicy } from '../../services/ossService';
import type { UploadFile as AntUploadFile } from 'antd/es/upload/interface';
import axios from 'axios';

const { TextArea } = Input;

const Container = styled.div<{ isFullscreen?: boolean }>`
    background: #fff;
    border-radius: ${props => props.isFullscreen ? '0' : '8px'};
    padding-top: ${props => props.isFullscreen ? '24px' : '16px'};
    max-width: ${props => props.isFullscreen ? '100%' : '600px'};
    display: flex;
    flex-direction: column;
    ${props => props.isFullscreen && `
        height: 100vh;
        width: 100%;
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
    `}
`;


const StyledForm = styled(Form) <{ isFullscreen?: boolean }>`
    display: flex;
    flex-direction: column;
    height: ${props => props.isFullscreen ? '100%' : 'auto'};
    
    .ant-form-item {
        margin-bottom: 24px;
    }

    .ant-form-item-label {
        padding-bottom: 8px;
        
        label {
            font-size: 14px;
            color: #1f1c2b;
        }
    }
    
    .ant-input, .ant-input-textarea {
        border-radius: 4px;
        border: 1px solid #e8e8e8;
        
        &:focus, &:hover {
            border-color: #1890ff;
            box-shadow: none;
        }
    }
    
    .ant-form-item-explain {
        font-size: 10px;
    }
`;

const FormContent = styled.div<{ isFullscreen?: boolean }>`
    display: flex;
    flex-direction: column;
    ${props => props.isFullscreen && `
        flex: 1;
        overflow: hidden;
        height: calc(100vh - 120px);
    `}
`;

const QuestionList = styled.div<{ isFullscreen?: boolean }>`
    padding: ${props => props.isFullscreen ? '16px 24px' : '0 5px'};
    ${props => props.isFullscreen ? `
        height: 100%;
        overflow-y: auto;
    ` : `
        max-height: 60vh;
        overflow-y: auto;
    `}
    overflow-x: hidden;
    
    &::-webkit-scrollbar {
        width: 6px;
    }
    
    &::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 3px;
    }
    
    &::-webkit-scrollbar-thumb {
        background: #ccc;
        border-radius: 3px;
    }
    
    &::-webkit-scrollbar-thumb:hover {
        background: #aaa;
    }
`;

const ButtonGroup = styled.div<{ isFullscreen?: boolean }>`
    display: flex;
    gap: 8px;
    margin-top: ${props => props.isFullscreen ? '100px' : '16px'};
    padding: ${props => props.isFullscreen ? '12px 24px' : '0 16px'};
    flex-shrink: 0;
    ${props => props.isFullscreen && `
        background: #fff;
        border-top: 1px solid #f0f0f0;
        position: sticky;
        bottom: 0;
        z-index: 100;
    `}

    button {
        flex: 1;
        height: 32px;
        font-size: 12px;
        border-radius: 14px;
        max-width: 120px;
        margin: 0 auto;
    }
`;

const ProgressContainer = styled.div<{ isFullscreen?: boolean }>`
    margin-bottom: ${props => props.isFullscreen ? '0' : '24px'};
    padding: ${props => props.isFullscreen ? '12px 24px' : '0 16px'};
    flex-shrink: 0;
    ${props => props.isFullscreen && `
        background: #fff;
        border-bottom: 1px solid #f0f0f0;
        position: sticky;
        top: 0;
        z-index: 100;
    `}

    .progress-info {
        display: flex;
        justify-content: space-between;
        margin-bottom: 8px;
        color: #666;
        font-size: 14px;
    }

    .ant-progress-outer {
        padding-right: 0 !important;
        margin-right: 0 !important;
    }

    .ant-progress-text {
        display: none;
    }
`;

const OptionsContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
`;

const UploadContainer = styled.div`
    .ant-upload-list-picture-card {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
    }

    .ant-upload-list-picture-card-container {
        width: 100px !important;
        height: 100px !important;
    }

    .ant-upload.ant-upload-select {
        width: 100px !important;
        height: 100px !important;
    }
`;

const QuestionLabel = styled.div`
    font-weight: bold;
    margin-bottom: 8px;
    
    .optional {
        font-weight: normal;
        color: #999;
        margin-left: 4px;
    }
`;

interface Question {
    questionId: string;
    question: string;
    type: number; // 0: 单选, 1: 多选, 2: 文本, 3: 图片
    required: boolean;
    describe?: string;
    options?: Array<{
        optionId: string;
        questionOption: string;
    }>;
    limitOptionsNumber?: number;
}



interface FillProps {
    recordId: string;
    templateId: string;
    onSubmit?: () => void;
    onScrollToCurrentMessage?: () => void;
    isFullscreen?: boolean;
    onSendMessage?: (text: string) => void;
    messageId?: string;
    initialState?: any;
    onStateChange: (state: any) => void;
}

const LoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 200px;
`;

const ErrorContainer = styled.div`
    padding: 20px;
`;

const Fill: React.FC<FillProps> = ({
    recordId,
    templateId,
    onSubmit,
    onScrollToCurrentMessage,
    onSendMessage,
    isFullscreen,
    initialState,
    onStateChange
}) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [currentGroupIndex, setCurrentGroupIndex] = useState(() => {
        return initialState?.currentGroupIndex || 0;
    });
    const [uploadLoading, setUploadLoading] = useState<{ [key: string]: boolean }>({});
    const [fileListMap, setFileListMap] = useState<{ [key: string]: any[] }>({});
    const [formData, setFormData] = useState<TemplateData | null>(null);
    const [loadingTemplate, setLoadingTemplate] = useState(true);
    const [answers, setAnswers] = useState<{ [key: string]: any }>(() => {
        return initialState?.answers || {};
    });

    // 加载模板数据
    useEffect(() => {
        const loadTemplateData = async () => {
            try {
                const data = await getTemplateData(templateId);
                if (data) {
                    setFormData(data);
                    if (initialState?.answers) {
                        form.setFieldsValue(initialState.answers);
                    }
                } else {
                    message.error('获取问诊模板失败');
                }
            } catch (error) {
                console.error('加载模板数据失败:', error);
                message.error('加载模板数据失败');
            } finally {
                setLoadingTemplate(false);
            }
        };

        loadTemplateData();
    }, [templateId, form, initialState]);

    // 更新状态的函数
    const updateState = useCallback((newAnswers: { [key: string]: any } = answers) => {
        // 使用防抖，避免频繁更新
        const state = {
            currentGroupIndex,
            answers: newAnswers,
            templateId,
            recordId
        };
        onStateChange?.(state);
    }, [currentGroupIndex, answers, templateId, recordId, onStateChange]);

    const handleUpload = async (options: any, questionId: string) => {
        const { file, onSuccess, onError } = options;

        try {
            setUploadLoading(prev => ({
                ...prev,
                [questionId]: true
            }));

            // 获取 Policy 签名信息
            const policy = await getOSSPolicy('picture');

            // 构建文件名和key
            const fileName = `${Date.now()}-${file.name}`;
            const key = `${policy.dir}/${fileName}`;

            // 构建 FormData
            const formData = new FormData();
            formData.append('key', key);
            formData.append('OSSAccessKeyId', policy.keyId);
            formData.append('policy', policy.policy);
            formData.append('Signature', policy.signature);
            formData.append('success_action_status', '200');
            formData.append('file', file);

            // 发送上传请求
            await axios.post(policy.host, formData);

            // 创建预览图片的 URL
            let thumbUrl = '';
            if (file.type?.startsWith('image/')) {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                await new Promise((resolve) => {
                    reader.onload = () => {
                        thumbUrl = reader.result as string;
                        resolve(null);
                    };
                });
            }

            // 构建文件信息
            const fileInfo = {
                uid: file.uid,
                name: file.name,
                status: 'done',
                thumbUrl,
                type: file.type,
                key: key
            };

            console.log('Upload success, file info:', fileInfo);  // 调试日志
            onSuccess?.(fileInfo);

        } catch (error) {
            console.error('上传失败:', error);
            onError?.(error as Error);
            message.error('上传失败');
        } finally {
            setUploadLoading(prev => ({
                ...prev,
                [questionId]: false
            }));
        }
    };

    const handleFileListChange = (questionId: string, fileList: AntUploadFile[]) => {
        // 更新文件列表
        setFileListMap(prev => ({
            ...prev,
            [questionId]: fileList
        }));

        // 获取所有已上传完成的图片的key，并组装完整URL
        const urls = fileList
            .filter(file => file.status === 'done' && file.response?.key)  // 确保有response和key
            .map(file => file.url)
            .join(',');

        console.log('Assembled URLs:', urls);  // 调试日志

        // 更新表单值并提交
        form.setFieldValue(questionId, urls);
        handleQuestionChange(questionId, urls, {
            questionId,
            question: '',
            type: 3,
            required: false
        });
    };

    const handleQuestionChange = async (questionId: string, value: any, question: Question) => {
        try {
            // 保存问题答案到后端
            await submitConsultationForm({
                recordId: recordId || '',
                finish: false,
                questions: [{
                    questionId,
                    answer: Array.isArray(value) ? value.join(',') : value || '',
                    otherAnswer: '',
                    serious: false,
                    question: question.question,
                    type: question.type
                }]
            });

            // 只在这里更新一次状态
            const newAnswers = {
                ...answers,
                [questionId]: value
            };
            setAnswers(newAnswers);
            updateState(newAnswers);
        } catch (error) {
            console.error('保存问题回答失败:', error);
        }
    };

    const handleSubmit = async () => {
        try {
            setLoading(true);

            // 直接提交表单
            await submitConsultationForm({
                recordId,
                finish: true,
                questions: []
            });

            // 发送消息
            onSendMessage?.("问诊单我填完了");

            // 换到成功状态
            onSubmit?.();
        } catch (error) {
            message.error('提交失败');
            console.error('提交问诊表单失败:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleNext = async () => {
        try {
            if (!formData) return;
            
            const currentQuestions = formData.typeList[currentGroupIndex].questions;
            const fields = currentQuestions.map(q => q.questionId);
            await form.validateFields(fields);

            if (currentGroupIndex < formData.typeList.length - 1) {
                const nextIndex = currentGroupIndex + 1;
                setCurrentGroupIndex(nextIndex);
                
                // 使用新的 index 更新状态
                const state = {
                    currentGroupIndex: nextIndex,  // 使用新的 index
                    answers,
                    templateId,
                    recordId
                };
                onStateChange?.(state);

                setTimeout(() => {
                    onScrollToCurrentMessage?.();
                }, 200);
            } else {
                handleSubmit();
            }
        } catch (error) {
            console.error('表单验证失败:', error);
        }
    };

    const handlePrev = () => {
        if (currentGroupIndex > 0) {
            const prevIndex = currentGroupIndex - 1;
            setCurrentGroupIndex(prevIndex);

            // 使用新的 index 更新状态
            const state = {
                currentGroupIndex: prevIndex,  // 使用新的 index
                answers,
                templateId,
                recordId
            };
            onStateChange?.(state);

            setTimeout(() => {
                onScrollToCurrentMessage?.();
            }, 200);
        }
    };

    const renderQuestion = (question: Question) => {
        const label = (
            <QuestionLabel>
                {question.question}
                {!question.required && <span className="optional">(可选)</span>}
            </QuestionLabel>
        );

        switch (question.type) {
            case 0: // 单选
                return (
                    <Form.Item
                        key={question.questionId}
                        label={label}
                        name={question.questionId}
                        rules={[{ required: question.required, message: '请选择' }]}
                    >
                        <Radio.Group onChange={(e) => handleQuestionChange(question.questionId, e.target.value, question)}>
                            <OptionsContainer>
                                {question.options?.map(option => (
                                    <Radio key={option.optionId} value={option.questionOption}>
                                        {option.questionOption}
                                    </Radio>
                                ))}
                            </OptionsContainer>
                        </Radio.Group>
                    </Form.Item>
                );
            case 1: // 多选
                return (
                    <Form.Item
                        key={question.questionId}
                        label={label}
                        name={question.questionId}
                        rules={[{ required: question.required, message: '请选择' }]}
                    >
                        <Checkbox.Group onChange={(values) => handleQuestionChange(question.questionId, values, question)}>
                            <OptionsContainer>
                                {question.options?.map(option => (
                                    <Checkbox key={option.optionId} value={option.questionOption}>
                                        {option.questionOption}
                                    </Checkbox>
                                ))}
                            </OptionsContainer>
                        </Checkbox.Group>
                    </Form.Item>
                );
            case 2: // 文本
                return (
                    <Form.Item
                        label={label}
                        name={question.questionId}
                        rules={[{ required: question.required, message: '请输入' }]}
                    >
                        <TextArea
                            rows={2}
                            placeholder="请输入"
                            onBlur={(e) => handleQuestionChange(question.questionId, e.target.value, question)}
                        />
                    </Form.Item>
                );
            case 3: // 图片
                return (
                    <Form.Item
                        label={label}
                        name={question.questionId}
                        rules={[{ required: question.required, message: '请上传图片' }]}
                    >
                        <UploadContainer>
                            <Upload
                                listType="picture-card"
                                customRequest={(options) => handleUpload(options, question.questionId)}
                                multiple={true}
                                accept="image/*"
                                fileList={fileListMap[question.questionId] || []}
                                onChange={({ fileList }) => {
                                    // 保持所有文件的状态
                                    const newFileList = fileList.map(file => {
                                        if (file.status === 'done' && !file.url && file.response) {
                                            // 如果文件上传完成但没有url，添加url
                                            return {
                                                ...file,
                                                url: `https://consultation-backend.zhi-yuan.net/api/v1/aliyun/authorization?objectKey=${file.response.key}`
                                            };
                                        }
                                        return file;
                                    });
                                    handleFileListChange(question.questionId, newFileList);
                                }}
                                onRemove={(file) => {
                                    const newFileList = (fileListMap[question.questionId] || []).filter(
                                        item => item.uid !== file.uid
                                    );
                                    handleFileListChange(question.questionId, newFileList);
                                    return true;
                                }}
                            >
                                {uploadLoading[question.questionId] ? (
                                    <LoadingOutlined />
                                ) : (
                                    <div>
                                        <PlusOutlined />
                                        <div style={{ marginTop: 8 }}>上传</div>
                                    </div>
                                )}
                            </Upload>
                        </UploadContainer>
                    </Form.Item>
                );
            default:
                return null;
        }
    };

    const currentGroup = formData?.typeList[currentGroupIndex];
    const progress = formData ? ((currentGroupIndex + 1) / formData.typeList.length) * 100 : 0;

    if (loadingTemplate) {
        return (
            <Container isFullscreen={isFullscreen}>
                <LoadingContainer>
                    <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
                </LoadingContainer>
            </Container>
        );
    }

    if (!formData) {
        return (
            <Container isFullscreen={isFullscreen}>
                <ErrorContainer>
                    <Alert
                        message="错误提示"
                        description="获取问诊模板失败"
                        type="error"
                        showIcon
                    />
                </ErrorContainer>
            </Container>
        );
    }

    return (
        <Container isFullscreen={isFullscreen}>
            <ProgressContainer isFullscreen={isFullscreen}>
                <div className="progress-info">
                    <span>{currentGroup?.typeName}</span>
                    <span className="progress-step">{currentGroupIndex + 1}/{formData.typeList.length}</span>
                </div>
                <Progress percent={progress} showInfo={false} strokeColor="#1890ff" />
            </ProgressContainer>

            <StyledForm form={form} layout="vertical" isFullscreen={isFullscreen}>
                <FormContent isFullscreen={isFullscreen}>
                    <QuestionList isFullscreen={isFullscreen}>
                        {currentGroup?.questions.map((question, index) => (
                            <div key={question.questionId || index}>
                                {renderQuestion(question)}
                            </div>
                        ))}
                    </QuestionList>
                </FormContent>

                <ButtonGroup isFullscreen={isFullscreen}>
                    {currentGroupIndex > 0 && (
                        <Button onClick={handlePrev}>
                            上一步
                        </Button>
                    )}
                    <Button
                        type="primary"
                        onClick={handleNext}
                        loading={loading}
                    >
                        {currentGroupIndex === formData.typeList.length - 1 ? '提交' : '下一步'}
                    </Button>
                </ButtonGroup>
            </StyledForm>
        </Container>
    );
};

export default Fill; 